// src/encounter/intelligence-dx-suggestions.tsx
//
// PROD-3041 · frame v2. Scrips Intelligence DIAGNOSIS SUGGESTIONS in the right Intelligence rail. They
// were the A&P rail's "Suggest" button and its ranked rows with confidence bars inside the Diagnoses
// block (live dev review, 6 Oct, F2). The A&P tool itself stays out of the rail (D-31); only the
// suggestions moved here.
//
// THE SAME SOURCE: `useAiDiagnosisSearch` (GET /api/AISearch/Diagnosis?search=, traced from Flutter),
// asked for the visit's chief complaint, the text the old Suggest read. Fetched only when the clinician
// asks ("Suggest diagnoses"), never on its own, as Flutter does.
//
// RULES HELD HERE (encounter authority §4 rule 3; UX laws L4, L5):
//   · a suggestion is inert until the clinician presses Add; Add records the diagnosis through the
//     A&P's own add (the host's handler, the same save path as the block's add line);
//   · the source is said once, above the list ("From the chief complaint …");
//   · no score, no percentage, no bar;
//   · after the provider sign (rule 38) nothing is added: the rows read, the Add action is gone;
//   · user copy never says "AI": the rail is Scrips Intelligence.
import { useState } from 'react';
import { Button, ClinicalTermRow, type CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import { useAiDiagnosisSearch } from './services/use-ai-diagnosis-search';
import { ADD_LINE_MAX_ROWS } from './lib/add-line-sections';

export const DX_SUGGEST_TITLE = 'Diagnosis suggestions';
export const DX_SUGGEST_ASK = 'Suggest diagnoses';
export const DX_SUGGEST_NO_COMPLAINT = 'Record a chief complaint to get diagnosis suggestions.';
export const DX_SUGGEST_LOADING = 'Finding suggestions…';
export const DX_SUGGEST_ERROR = "Suggestions couldn't be loaded.";
export const DX_SUGGEST_EMPTY = 'No suggestions for this chief complaint.';
export const DX_SUGGEST_ADDED = 'Added to this visit';

export interface IntelligenceDxSuggestionsProps {
  /** The visit's chief complaints (the SAME list HPI draws). */
  complaints: readonly string[];
  /** The visit's diagnoses: a suggestion whose code is already there reads "Added", with no Add. */
  existingCodes: readonly { code?: string | null }[];
  /** Records the diagnosis (the A&P's own add). */
  onAdd: (dx: CodeSearchResult) => void;
  /** After the provider sign: no Add, and no new ask. */
  readOnly?: boolean;
  /** Frame v2 rail (round 18r): with no chief complaint, nothing is drawn (no bare "Record a chief complaint" line). */
  quietWithoutComplaint?: boolean;
  /** The section's frame classes (the rail draws it as a card). */
  className?: string;
}

const norm = (code: string) => code.trim().toUpperCase();

export function IntelligenceDxSuggestions({
  complaints,
  existingCodes,
  onAdd,
  readOnly = false,
  quietWithoutComplaint = false,
  className = 'flex flex-col gap-2',
}: IntelligenceDxSuggestionsProps) {
  const source = complaints.map((c) => c.trim()).filter(Boolean).join(', ');
  // The ask belongs to ONE complaint text: a changed complaint is not asked until the clinician asks again.
  const [askedFor, setAskedFor] = useState<string | null>(null);
  const asked = !!source && askedFor === source;
  const result = useAiDiagnosisSearch(source, asked);
  const taken = new Set(existingCodes.map((d) => norm(d.code ?? '')));
  const rows = (result.data ?? []).slice(0, ADD_LINE_MAX_ROWS);

  if (quietWithoutComplaint && !source) return null;

  return (
    <section aria-label={DX_SUGGEST_TITLE} data-slot="intelligence-dx-suggestions" className={className}>
      <h3 className="m-0 text-size-sm font-semibold text-text-primary">{DX_SUGGEST_TITLE}</h3>
      {!source ? (
        <p className="m-0 text-size-sm text-text-secondary">{DX_SUGGEST_NO_COMPLAINT}</p>
      ) : (
        <>
          <p data-slot="dx-suggest-source" className="m-0 text-size-sm text-text-secondary">
            From the chief complaint: <span className="text-text-primary">{source}</span>
          </p>
          {!asked && !readOnly && (
            <span>
              <Button variant="secondary" size="sm" onClick={() => setAskedFor(source)}>
                {DX_SUGGEST_ASK}
              </Button>
            </span>
          )}
          {asked && result.isFetching && rows.length === 0 && (
            <p role="status" className="m-0 text-size-sm text-text-secondary">
              {DX_SUGGEST_LOADING}
            </p>
          )}
          {asked && result.isError && (
            <p role="status" className="m-0 flex items-center gap-2 text-size-sm text-text-secondary">
              {DX_SUGGEST_ERROR}
              {/* Codex P2, 6 Oct: a failed ask can be asked again for the same complaint. */}
              {!readOnly && (
                <Button variant="tertiary" size="sm" onClick={() => void result.refetch()}>
                  Retry
                </Button>
              )}
            </p>
          )}
          {asked && !result.isFetching && !result.isError && rows.length === 0 && (
            <p role="status" className="m-0 text-size-sm text-text-secondary">
              {DX_SUGGEST_EMPTY}
            </p>
          )}
          {asked && rows.length > 0 && (
            <ul aria-label={DX_SUGGEST_TITLE} className="m-0 flex list-none flex-col p-0">
              {rows.map((s) => {
                const added = taken.has(norm(s.code));
                return (
                  <ClinicalTermRow
                    key={s.id}
                    kind="dx"
                    term={s.display}
                    code={s.code}
                    nameOverflow="wrap"
                    showDot={false}
                    readOnly={readOnly || added}
                    {...(added ? { secondary: DX_SUGGEST_ADDED } : {})}
                    actions={
                      readOnly || added
                        ? undefined
                        : [
                            {
                              label: 'Add',
                              onClick: () => onAdd({ id: s.id, code: s.code, display: s.display, system: 'ICD-10' }),
                            },
                          ]
                    }
                  />
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
